// ============================================================================
// Columns for the two leg-rooted sections of the Last Mile board — MISSING ORDERS
// (top) and AWAITING PAYMENT (bottom). Both are deliveries past their promise
// with no truck; they are split because only the first is OUR failure. The rows
// are the same shape and the same columns serve both, so the split is a partition
// in `board.tsx`, not a second table.
//
// Every cell here is the board's cell, imported from `board_columns` rather than
// re-written: the PO stack, the SO number + Open link + priority pills + floor /
// lift line, `locationCell` for From / To, the Customer block, `escalationCell`.
// A Missing Orders row therefore reads as a board row, because it IS rendered by the same
// code. That is the point — this is a section of the board, not a widget above it.
//
// Three columns differ, and only because the row genuinely answers a different
// question:
//   - DISPATCH → "Promised". There is no dispatch window; a truck was never
//     booked. The promise, and how far past it we are, sits in that slot.
//   - STATUS → the REASON. A board row's status is its delivery status; a row
//     here has no delivery to have a status. What belongs there is why nothing has
//     shipped — "Pick error", "First mile pending" — with the backend's own error
//     text as the tooltip, which is the bit that says which team to call.
//   - ACTION → escalate / resolve only.
//
// Carrier, Driver & vehicle, Owner and Delhivery are dropped outright. They are
// booking fields, and a row here has no booking: rendering them would spend ~700px
// on four permanently-empty columns, and wiring their actions would point a write
// at a booking id that does not exist.
// ============================================================================
import { FlagFilled, WarningOutlined } from "@ant-design/icons";
import { Button, Space, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import React from "react";
import { MissingOrderRow } from "./logistics.api";
import {
  customerCell,
  dash,
  escalationCell,
  locationCell,
  poCell,
  salesOrderCell,
} from "./board_columns";
import { fmtIST } from "../format";

// Reason → colour, mirroring the precedence the server already applies: an ERROR or a HOLD needs a
// named human, a leg merely not worked yet is queue time, and `awaiting_booking` is the one row ops
// can clear from this very board without chasing anybody.
const REASON_COLOR: Record<string, string> = {
  awaiting_booking: "blue",
  error: "error",
  held: "gold",
  // Not a fulfilment failure at all — the leg is gated shut on purpose until the money lands. Neutral
  // on purpose: these rows live in their own section and must not read as something going wrong.
  payment_pending: "default",
  po_pending: "volcano",
  first_mile_pending: "orange",
  // The truck already ran — the goods are on our own floor, unreceipted. A warehouse job, not a
  // pickup to chase, so it reads differently from first_mile_pending.
  grn_pending: "gold",
  supplier_delivery_pending: "orange",
  transfer_pending: "geekblue",
  pick_pending: "purple",
  packing_pending: "purple",
  invoice_pending: "cyan",
  service_pending: "cyan",
  blocked: "default",
  no_plan: "red",
};

// What the reason means, when the server has no error text of its own to show. The tooltip prefers
// `blocking_detail` — the exact backend failure — because that is what makes the row actionable.
const REASON_HINT: Record<string, string> = {
  awaiting_booking:
    "Everything upstream has cleared — this one only needs a truck. Book it from this board.",
  error: "A leg is failing. Somebody has to fix it before the goods can move.",
  held: "A leg is waiting on something external (a batch pin, a Zoho sync, a decision). Not a failure — but nothing moves until it clears.",
  payment_pending:
    "The order is not fully paid, so the outbound leg is deliberately gated shut. Nothing is stuck — this clears itself the moment the payment lands or is approved.",
  po_pending: "The purchase order has not gone to the supplier yet.",
  first_mile_pending: "The goods have not been collected from the supplier yet.",
  grn_pending:
    "Collected — the first mile is done. The goods are at our dock and the GRN has not been filed yet; it is on the Pending GRN queue.",
  supplier_delivery_pending:
    "The vendor is delivering this themselves and has not arrived. Chase the supplier.",
  transfer_pending: "The line-haul to the fulfilment warehouse has not happened yet.",
  pick_pending: "The warehouse floor has not picked this yet.",
  packing_pending: "Picked, but not packed yet.",
  invoice_pending: "The customer invoice has not been raised yet.",
  service_pending: "The site work has not been confirmed as performed.",
  blocked: "An upstream leg is still outstanding.",
  no_plan: "This allotment has no open leg at all — its plan needs looking at.",
};

// SOMEBODY IS ALREADY ON IT. The point of this tag is to stop a second operator chasing an order a
// first one has already escalated — so it counts escalations open on ANY still-outstanding leg of the
// allotment, not just the delivery leg. Most are raised upstream (on the pick from the picklist queue,
// on the inbound truck from First Mile), which is exactly the case the delivery desk cannot otherwise
// see, and exactly the case where the next move belongs to another team.
//
// `active_escalations` already encodes what "currently escalated" means (latest log event is a raise,
// unresolved, on a leg that is still owed) — see the field's note in `logistics.api.ts`. Nothing is
// re-derived here.
const EscalatedTag: React.FC<{ row: MissingOrderRow }> = ({ row }) => {
  const open = row.active_escalations ?? [];
  if (!open.length) return null;
  const upstream = open.filter((e) => !e.is_delivery_leg);
  const label =
    open.length === 1
      ? `escalated${upstream.length ? ` · ${upstream[0].operation_type.replace(/_/g, " ")}` : ""}`
      : `escalated · ${open.length} legs`;
  return (
    <Tooltip
      title={
        <>
          {open.map((e, i) => (
            <div key={i}>
              {e.operation_type.replace(/_/g, " ")}: {e.issue_type || "escalation"}
              {e.count > 1 ? ` (${e.count}\u00d7 raised)` : ""}
              {e.raised_by ? ` \u2014 ${e.raised_by}` : ""}
            </div>
          ))}
          <div style={{ marginTop: 4, opacity: 0.75 }}>
            Already flagged and not yet resolved — the next move is with the team that owns that leg.
          </div>
        </>
      }
    >
      {/* MAGENTA + a flag, deliberately. Its neighbours in this cell are express (red), unloading
          (volcano) and outstation (orange) — one warm family — so volcano made this read as a fourth
          shade of the same thing and vanish among them. Magenta is the one preset not already spoken
          for here (intercity has geekblue), and the icon gives it a second channel to be told apart
          by, the way the thunderbolt and the download arrow do for its neighbours.

          marginTop sits on the tag itself, not on a wrapper: the component returns null when nothing
          is open, so a wrapper would leave dead space on every un-escalated row. */}
      <Tag color="magenta" icon={<FlagFilled />} style={{ marginInlineEnd: 0, marginTop: 3 }}>
        {label}
      </Tag>
    </Tooltip>
  );
};

export interface MissingColumnHandlers {
  onRaiseIssue: (r: MissingOrderRow) => void;
  onResolveIssue: (r: MissingOrderRow) => void;
  // Same two permissions the board's own columns gate on, so a read-only operator sees the identical
  // row here and there — it just isn't theirs to act on.
  canUpdate?: boolean;
  canResolveEscalation?: boolean;
  // Mount the escalation-detail column. Passed only when at least one row HAS an escalation — the
  // same reasoning `buildColumns`' `showEscalation` follows: no section should pay 220px for a field
  // that is null on every one of its rows.
  showEscalation?: boolean;
}

export function buildMissingColumns(h: MissingColumnHandlers): ColumnsType<MissingOrderRow> {
  return [
    ...(h.showEscalation
      ? [
          {
            title: "Escalation",
            key: "escalation",
            width: 220,
            fixed: "left",
            // The board's own cell. It reads `escalation` off the row and nothing else, so it works
            // here unchanged — one definition of what a flagged row looks like, everywhere.
            render: (_: unknown, r: MissingOrderRow) =>
              escalationCell(r, h.onResolveIssue, !!h.canResolveEscalation),
          } as ColumnsType<MissingOrderRow>[number],
        ]
      : []),
    {
      // The board's own cells, called — not copied. A Missing Orders row therefore renders identically to a
      // booking row in these three columns, and stays identical when they change.
      title: "PO #",
      key: "po",
      width: 140,
      fixed: "left",
      render: (_, r) => poCell(r),
    },
    {
      // The board's own SO cell, with the ESCALATED tag stacked under it. It sits here rather than in
      // Status because it is a fact about the ORDER ("somebody is already on this"), not about what
      // is blocking the delivery — and Status is already carrying the reason plus the stuck-at leg.
      title: "Sales order",
      key: "order",
      width: 200,
      fixed: "left",
      render: (_, r) => (
        <div style={{ minWidth: 0 }}>
          {salesOrderCell(r)}
          <EscalatedTag row={r} />
        </div>
      ),
    },
    {
      // The Dispatch slot, answering what a Missing Orders row actually has: the promise, and how far past
      // it we are. Due TODAY is called out differently from a promise already broken — it is the
      // last chance to make it, and collapsing the two would bury the ones still saveable.
      title: "Promised",
      key: "promised",
      width: 100,
      render: (_, r) => {
        if (!r.target_delivery_date) return dash(null);
        const late = r.days_late ?? 0;
        return (
          <div style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            <div style={{ color: late > 0 ? "#cf1322" : "#d46b08", fontWeight: 600 }}>
              {fmtIST(r.target_delivery_date, "DD MMM")}
            </div>
            <div style={{ color: late > 0 ? "#cf1322" : "#888" }}>
              {late > 0 ? `${late}d late` : "today"}
            </div>
          </div>
        );
      },
    },
    {
      // The Status slot. A board row shows its delivery status; this row has no delivery to have
      // one, so what belongs here is WHY nothing has shipped — and the tooltip carries the backend's
      // own error text, which is the part that names the team to call.
      title: "Status",
      key: "status",
      width: 190,
      render: (_, r) => (
        <Space size={[4, 4]} wrap>
          <Tooltip title={r.blocking_detail || REASON_HINT[r.reason_code] || undefined}>
            <Tag
              color={REASON_COLOR[r.reason_code] || "default"}
              style={{ marginInlineEnd: 0, whiteSpace: "normal" }}
            >
              {r.reason}
            </Tag>
          </Tooltip>
          {/* Which leg it is stuck at, under the reason — the same stacked treatment the board's
              status cell gives its overlays. */}
          {r.blocking_operation_type && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              at {r.blocking_operation_type.replace(/_/g, " ")}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      // The board's own location cells, pin links included.
      title: "From",
      key: "from",
      width: 150,
      render: (_, r) => locationCell(r.origin_label, r.origin_lat, r.origin_long),
    },
    {
      title: "To",
      key: "to",
      width: 150,
      render: (_, r) => locationCell(r.destination_label, r.destination_lat, r.destination_long),
    },
    {
      // WHO WE BUY FROM, next to who we sell to. Not a column the board itself has — a last-mile
      // booking ships from our own warehouse, so its From/To never names the supplier. It belongs
      // here because on these rows the blocker is usually upstream: a reason of "Supplier has not
      // delivered" or "First mile pending" is a phone call, and this is the name to call.
      title: "Supplier",
      key: "supplier",
      width: 170,
      render: (_, r) => (
        <Tooltip title={r.supplier_name ?? undefined}>
          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {r.supplier_name || dash(null)}
          </div>
        </Tooltip>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      width: 160,
      render: (_, r) => customerCell(r),
    },
    {
      title: "Qty",
      key: "qty",
      width: 100,
      render: (_, r) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {r.qty}{" "}
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            ({r.item_count})
          </Typography.Text>
        </span>
      ),
    },
    {
      // Escalating is the only thing that can be done to a row with no truck — and it is the right
      // thing: the fix lives with whichever team owns the blocking leg, not here.
      title: "Action",
      key: "action",
      width: 150,
      render: (_, r) =>
        !h.canUpdate ? (
          dash(null)
        ) : (
          <Space size={4} wrap onClick={(e) => e.stopPropagation()}>
            {r.escalation ? (
              <>
                {h.canResolveEscalation && (
                  <Button type="link" size="small" onClick={() => h.onResolveIssue(r)}>
                    Resolve
                  </Button>
                )}
                <Tooltip title="Flag it again — a second raise re-notifies the teams and bumps the count on the row.">
                  <Button type="link" size="small" danger onClick={() => h.onRaiseIssue(r)}>
                    Re-raise
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Button
                type="link"
                size="small"
                danger
                icon={<WarningOutlined />}
                onClick={() => h.onRaiseIssue(r)}
              >
                Escalate
              </Button>
            )}
          </Space>
        ),
    },
  ];
}
